import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  CircularProgress,
  Fade,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  RocketLaunch,
  AccountCircle,
  EventNote,
  CalendarMonth,
  Notifications,
  Videocam,
  TrackChanges,
  Security,
  Build,
  Help,
  TrendingUp,
  ArrowForward,
  Clear,
} from '@mui/icons-material';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';

import { getUserDetails } from '../utils';
import {
  useQueryGetHelpCategories,
  useQueryGetHelpArticles,
  useQueryGetPopularArticles,
} from '../hooks/react-query/helpArticles';

const CATEGORY_ICONS = {
  RocketLaunch,
  AccountCircle,
  PeopleAlt,
  EventNote,
  CalendarMonth,
  Videocam,
  RecordVoiceOver,
  TrackChanges,
  Notifications,
  AdminPanelSettings,
  Security,
  Build,
  Help,
};

function HelpCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userDetails = getUserDetails();
  const userToken = userDetails?.token;

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  // Fetch categories
  const { data: categoriesResponse, isLoading: categoriesLoading } = useQueryGetHelpCategories(
    userToken,
    navigate,
    toast
  );

  // Fetch articles based on filters
  const { data: articlesResponse, isLoading: articlesLoading } = useQueryGetHelpArticles(
    {
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      search: debouncedSearch || undefined,
    },
    userToken,
    navigate,
    toast
  );

  // Fetch popular articles
  const { data: popularResponse } = useQueryGetPopularArticles(5, userToken, navigate, toast);

  const categories = categoriesResponse?.data?.data || [];
  const articles = articlesResponse?.data?.data || [];
  const popularArticles = popularResponse?.data?.data || [];

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (searchQuery) {
        setSearchParams({ search: searchQuery, category: selectedCategory });
      } else if (selectedCategory !== 'all') {
        setSearchParams({ category: selectedCategory });
      } else {
        setSearchParams({});
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchQuery('');
    if (categoryId !== 'all') {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const handleArticleClick = (articleSlug) => {
    navigate(`/help/${articleSlug}`);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSearchParams(selectedCategory !== 'all' ? { category: selectedCategory } : {});
  };

  const showArticles = debouncedSearch || selectedCategory !== 'all';

  return (
    <>
      <Helmet>
        <title>Help Center | Edifying Teachers</title>
      </Helmet>

      <Container 
        maxWidth={false} 
        sx={{ 
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          mx: 'auto'
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6, mt: 2 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: '#A94B9C',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            How can we help you?
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}>
            Search our knowledge base or browse categories to find answers
          </Typography>

          {/* Search Box */}
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Search for help articles, tutorials, and guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 28, color: '#A94B9C' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'white',
                  fontSize: '1.1rem',
                  padding: '8px',
                  boxShadow: '0 4px 20px rgba(169, 75, 156, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 30px rgba(169, 75, 156, 0.25)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 6px 30px rgba(169, 75, 156, 0.3)',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Categories Section */}
        {!showArticles && (
          <Fade in timeout={500}>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: 'text.primary', mb: 3, textAlign: 'center' }}
              >
                Browse by Category
              </Typography>

              {categoriesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={60} sx={{ color: '#A94B9C' }} />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {categories.map((category) => {
                    const IconComponent = CATEGORY_ICONS[category.icon] || Help;
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
                        <Card
                          sx={{
                            height: '100%',
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            border: '2px solid transparent',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: '0 12px 40px rgba(169, 75, 156, 0.2)',
                              borderColor: '#A94B9C',
                            },
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleCategoryClick(category.id)}
                            sx={{ height: '100%', p: 3 }}
                          >
                            <CardContent sx={{ textAlign: 'center' }}>
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mx: 'auto',
                                  mb: 2,
                                  boxShadow: '0 4px 20px rgba(169, 75, 156, 0.3)',
                                }}
                              >
                                <IconComponent sx={{ fontSize: 40, color: 'white' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                {category.name}
                              </Typography>
                              <Chip
                                label={`${category.count} article${category.count !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                  backgroundColor: alpha('#A94B9C', 0.1),
                                  color: '#A94B9C',
                                  fontWeight: 600,
                                }}
                              />
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Fade>
        )}

        {/* Articles Section */}
        {showArticles && (
          <Fade in timeout={500}>
            <Box>
              {/* Breadcrumb */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <Chip
                  label="All Categories"
                  onClick={() => handleCategoryClick('all')}
                  variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...(selectedCategory === 'all' && {
                      backgroundColor: '#A94B9C',
                      color: 'white',
                    }),
                  }}
                />
                {selectedCategory !== 'all' && (
                  <>
                    <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Chip
                      label={categories.find((c) => c.id === selectedCategory)?.name || selectedCategory}
                      sx={{
                        backgroundColor: '#A94B9C',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </>
                )}
              </Stack>

              {articlesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={60} sx={{ color: '#A94B9C' }} />
                </Box>
              ) : articles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Help sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h5" color="text.secondary">
                    No articles found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your search or browse other categories
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                      <CardContent sx={{ p: 0 }}>
                        <List disablePadding>
                          {articles.map((article, index) => (
                            <React.Fragment key={article._id}>
                              {index > 0 && <Divider />}
                              <ListItem disablePadding>
                                <ListItemButton
                                  onClick={() => handleArticleClick(article.slug)}
                                  sx={{
                                    py: 2.5,
                                    px: 3,
                                    '&:hover': {
                                      backgroundColor: alpha('#A94B9C', 0.05),
                                    },
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {article.title}
                                      </Typography>
                                    }
                                    secondary={
                                      <>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mb: 1, lineHeight: 1.6 }}
                                        >
                                          {article.excerpt}
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                          {article.tags?.slice(0, 3).map((tag) => (
                                            <Chip
                                              key={tag}
                                              label={tag}
                                              size="small"
                                              sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                backgroundColor: alpha('#A94B9C', 0.1),
                                                color: '#A94B9C',
                                              }}
                                            />
                                          ))}
                                        </Stack>
                                      </>
                                    }
                                  />
                                  <ArrowForward sx={{ color: '#A94B9C', ml: 2 }} />
                                </ListItemButton>
                              </ListItem>
                            </React.Fragment>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Sidebar */}
                  <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                      {/* Popular Articles */}
                      {popularArticles.length > 0 && (
                        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                          <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                              <TrendingUp sx={{ color: '#A94B9C' }} />
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Popular Articles
                              </Typography>
                            </Stack>
                            <List disablePadding>
                              {popularArticles.map((article, index) => (
                                <React.Fragment key={article._id}>
                                  {index > 0 && <Divider sx={{ my: 1 }} />}
                                  <ListItemButton
                                    onClick={() => handleArticleClick(article.slug)}
                                    sx={{
                                      borderRadius: 1,
                                      '&:hover': {
                                        backgroundColor: alpha('#A94B9C', 0.05),
                                      },
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {article.title}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary">
                                          {article.views} views
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </React.Fragment>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      )}

                      {/* Categories Widget */}
                      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                            All Categories
                          </Typography>
                          <Stack spacing={1}>
                            {categories.map((category) => (
                              <Chip
                                key={category.id}
                                label={`${category.name} (${category.count})`}
                                onClick={() => handleCategoryClick(category.id)}
                                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                                sx={{
                                  justifyContent: 'flex-start',
                                  cursor: 'pointer',
                                  ...(selectedCategory === category.id && {
                                    backgroundColor: '#A94B9C',
                                    color: 'white',
                                    fontWeight: 600,
                                  }),
                                }}
                              />
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Fade>
        )}
      </Container>
    </>
  );
}

export default HelpCenter;
